import React from 'react';
import { BarChart3, Bell, CreditCard, FileText, Globe2, LifeBuoy, MapPin, Users } from 'lucide-react';
import { STATUS_PALETTE } from './adminDesignSystem';

// Legacy color string → semantic tone key. Keeps existing call-sites
// working while routing through the shared STATUS_PALETTE.
const LEGACY_COLOR_TO_TONE = {
  green:  'active',
  red:    'urgent',
  blue:   'venue',
  orange: 'early',
  yellow: 'hold',
  violet: 'pending',
  grey:   'neutral',
  gray:   'neutral',
};

export const adminDetailCardStyle = {
  background: 'var(--surface)',
  borderRadius: 24,
  padding: 18,
  border: '1px solid var(--card-border)',
  boxShadow: 'var(--premium-shadow-main)',
};

export const adminDetailSectionStyle = {
  borderRadius: 20,
  border: '1px solid var(--card-border)',
  background: 'var(--surface)',
  boxShadow: 'var(--premium-shadow-nested)',
  padding: 16,
  display: 'grid',
  gap: 10,
};

export const adminDetailFieldCardStyle = {
  borderRadius: 16,
  border: '1px solid var(--card-border)',
  background: 'var(--surface)',
  boxShadow: 'var(--premium-shadow-nested)',
  padding: '12px 14px',
};

export const Card = React.forwardRef(({ children, style, ...props }, ref) => (
  <div ref={ref} style={{ ...adminDetailCardStyle, ...style }} {...props}>{children}</div>
));
Card.displayName = 'AdminCard';

export const Badge = ({ text, color, tone, 'data-testid': dataTestId }) => {
  const toneKey = tone || LEGACY_COLOR_TO_TONE[color] || 'neutral';
  const palette = STATUS_PALETTE[toneKey] || STATUS_PALETTE.neutral;
  return (
    <span
      data-testid={dataTestId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        alignSelf: 'flex-start',
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.1,
        background: palette.soft,
        color: palette.text,
        border: `1px solid ${palette.solid}22`,
      }}
    >
      {text}
    </span>
  );
};

export const Section = ({ title, children }) => (
  <div style={{ marginBottom: 14 }}>
    <p style={{ fontSize: 13, fontWeight: 700, color: '#999', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: 0.5 }}>{title}</p>
    {children}
  </div>
);

export const Field = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid var(--hairline)' }}>
    <span style={{ fontSize: 13, color: 'var(--text-2)' }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', maxWidth: '60%', textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</span>
  </div>
);

const hasDisplayValue = (value) => value != null && String(value).trim() !== '' && String(value).trim() !== '—';

export const AdminDetailSection = ({ title, items = [], children, dataTestId }) => {
  const visibleItems = items.filter((item) => hasDisplayValue(item?.value));
  if (!visibleItems.length && !children) return null;

  return (
    <div data-testid={dataTestId} style={adminDetailSectionStyle}>
      <p style={{ margin: 0, fontSize: 12, fontWeight: 800, color: 'var(--text-2)', letterSpacing: 0.6, textTransform: 'uppercase' }}>{title}</p>
      {visibleItems.map((item) => (
        <div key={`${title}-${item.label}`} style={adminDetailFieldCardStyle}>
          <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{item.label}</p>
          <p style={{ margin: '5px 0 0', fontSize: 14, fontWeight: 600, color: 'var(--text)', lineHeight: 1.4, wordBreak: 'break-word' }}>{item.value}</p>
        </div>
      ))}
      {children}
    </div>
  );
};

export const TimelinePreview = ({ timelineId, items, expandedTimelineId, setExpandedTimelineId }) => {
  const safeItems = (items || []).filter((item) => item?.label);
  const isExpanded = expandedTimelineId === timelineId;
  const visibleItems = isExpanded ? safeItems : safeItems.slice(0, 2);

  if (!safeItems.length) return null;

  return (
    <div style={{ marginTop: 14, borderTop: '1px solid var(--hairline)', paddingTop: 12 }}>
      <button onClick={(event) => { event.stopPropagation(); setExpandedTimelineId(isExpanded ? null : timelineId); }} data-testid={`admin-timeline-toggle-${timelineId}`} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: "'Outfit', sans-serif", fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Activity timeline</span>
        <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{isExpanded ? 'Hide' : 'Show more'}</span>
      </button>
      <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
        {visibleItems.map((item) => (
          <div key={`${timelineId}-${item.label}-${item.value}`} style={{ display: 'grid', gridTemplateColumns: '10px 1fr', gap: 10, alignItems: 'start' }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--text)', display: 'inline-block', marginTop: 4 }} />
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: '#111' }}>{item.label}</p>
              <p style={{ margin: '3px 0 0', fontSize: 12, color: '#666' }}>{item.value}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const adminTabs = [
  { id: 'dashboard', label: 'Overview', icon: BarChart3 },
  { id: 'users', label: 'Accounts', icon: Users },
  { id: 'support', label: 'Support Inbox', icon: LifeBuoy },
  { id: 'approving', label: 'Approving', icon: Bell },
  { id: 'venue-apps', label: 'Venues', icon: MapPin },
  { id: 'applications', label: 'Listings', icon: FileText },
  { id: 'maps', label: 'Maps', icon: Globe2 },
  { id: 'guests', label: 'Guests', icon: Users },
  { id: 'transactions', label: 'Finance', icon: CreditCard },
];